import { chunk } from 'lodash';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { IUser } from '../../../../ng2/shared/typings/interfaces/user.interface';
import { TValidUserRoles } from '../../../../ng2/shared/typings/interfaces/user.interface';
import nvSharedModelsModule from '../nv.shared.models.module';
import { ToggleService } from './../../../../ng2/shared/services/toggle/toggle.service';

export interface IStateData {
  roles: TValidUserRoles[];
  toggles?: string[];
  v2Only?: boolean;
}

angular
  .module(nvSharedModelsModule)
  .factory('User', function(
    $q,
    NvBaseModel,
    School,
    $window,
    Rollbar,
    $state,
    ObjectHash,
    $http,
    PublicConfig,
    UtilitiesService,
    ToggleService: ToggleService,
    ImUser: ImUser,
  ) {
    const PERMISSIONING_MODEL_NAME = 'USER';

    // eslint-disable-line new-cap
    const User = NvBaseModel(
      '/v1/users/:id',
      { id: '@_id' },
      {
        fetchUsersWithFullAccess: {
          method: 'GET',
          isArray: true,
          transformResponse: data => {
            const json = angular.fromJson(data);
            // if the request is cancelled, json will be null
            if (json) return json.data;
          },
          params: {
            where: '{ "authorizationStatus": { "$eq" : "FULL_ACCESS" } }',
          },
        },
      },
      null,
      PERMISSIONING_MODEL_NAME,
    );

    User.CLUSTER_ROLES = ['cluster_admin', 'cluster'];

    User.SCHOOL_ROLES = [
      'school_admin',
      'delegated_school_admin',
      'edit_all',
      'edit_caseload',
      'view_all',
      'view_caseload',
    ];

    // Fields that are required for the user to be considered "valid". Not a comprehensive list.
    User.REQUIRED_FIELDS = ['nvRole', '_id'];

    User.HUMAN_ROLE_NAMES = {
      school_admin: 'Admin',
      delegated_school_admin: 'Delegated Admin',
      edit_all: 'Edit All',
      edit_caseload: 'Edit Caseload',
      view_all: 'School Wide Viewer',
      view_caseload: 'Caseload Viewer',
    };

    User.singleUpdate = function(id, path, newValue) {
      const patch = UtilitiesService.setFieldByPath({}, path, newValue);

      return $http.patch(`${PublicConfig.NV_API_ORIGIN}/v1/users/${id}`, patch);
    };

    User.multiFieldUpdate = function(id, patch) {
      return $http.patch(`${PublicConfig.NV_API_ORIGIN}/v1/users/${id}`, patch);
    };

    User.getAllSchoolUsers = (schoolId: string) => {
      // Get the minimum fields needed to create a userMini
      const projection = {
        _id: true,
        gafeEmail: true,
        doeEmail: true,
        preferredName: true,
        name: true,
        delegatedRole: true,
        nvRole: true, // nvRole is returned regardless of whether it is in projection or not
      };

      return new Promise((resolve, reject) => {
        return User.fetchUsersWithFullAccess({
          schoolId,
          projection,
        })
          .$promise.then(users => {
            return resolve(users);
          })
          .catch(err => {
            return reject(err);
          });
      });
    };

    User.getMinisForSdcStore = (users: IUser[]) => {
      return users.map(user => {
        return ImUser.toMiniUser(user);
      });
    };

    User.extend({
      // Fetch all of the cluster-schools for the user and attach as _role_clusterSchools
      // Safe to call on cluster or non-cluster users (non-cluster users have an empty array)
      async join_role_clusterSchools() {
        if (!this._role_clusterSchools) {
          const clusterSchoolIds = this._role_clusterSchoolIds;
          // Requests for schools is being chunked to fix the issue described in https://newvisions.atlassian.net/browse/PFD-4533 (Carlos)
          const chunkedSchoolIds = chunk(clusterSchoolIds, 300); // we chunk schools
          const projection = JSON.stringify({
            _id: 1,
            nickName: 1,
            fullName: 1,
          });

          // send requests for schools in parallel (Carlos)
          await Promise.all(
            chunkedSchoolIds.map(async schoolIds => {
              const where = JSON.stringify({
                _id: {
                  $in: schoolIds,
                },
              });
              const url = `${
                PublicConfig.NV_API_ORIGIN
              }/v1/schools?limit=0&page=1&projection=${projection}&where=${where}`;
              // Fetching of schools was switched from using $resource to $http to fix the issue described in https://newvisions.atlassian.net/browse/PFD-4533 (Carlos)
              return $http.get(url);
            }),
          ).then(responses => {
            let clusterSchools = [];
            responses.forEach(response => {
              const data = response.data && response.data.data;
              if (data) clusterSchools = clusterSchools.concat(data);
            });
            this._role_clusterSchools = clusterSchools;
          });
        }

        return this._role_clusterSchools;
      },
    });

    return User;
  });
